import type { NarrativeForm } from './schema';
import type { CreateValuationInput } from '../hooks/useCreatePublicNarrative';
import type { UpdateValuationInput } from '../hooks/useUpdatePublicNarrative';
import type { ZodEffects, ZodTypeAny } from 'zod';
import { z } from 'zod';
import { INPUT_VALUE_RANGES } from './validation';

type TransformFormValuesToValuationProps = {
  values: NarrativeForm;
};

export const transformFormValuesToValuation = ({
  values,
}: TransformFormValuesToValuationProps) => {
  const {
    discountRatePercentage,
    fairValue,
    priceEarningsMultiple,
    profitMarginPercentage,
    revenuePercentage,
    timePeriod,
    futureSharesOutstanding,
    sharesOutstandingGrowthRate,
    exchangeRate,
  } = values;
  return {
    discountRate: discountRatePercentage,
    fairValue,
    futurePePercentageChange: priceEarningsMultiple,
    profitMarginPercentageChange: profitMarginPercentage,
    revenuePercentageChange: revenuePercentage,
    futureSharesOutstanding,
    timeframe: timePeriod,
    sharesOutstandingGrowthRate: sharesOutstandingGrowthRate,
    exchangeRate,
  } satisfies CreateValuationInput | UpdateValuationInput;
};

/**
 * Map of NarrativeForm fields to query params
 * "i" stands for initial, the rest tries to express the field name in a condensed way
 */
export const NARRATIVE_VALUATOR_QUERY_FIELD_MAP = {
  fairValue: {
    name: 'ifv',
    schema: z.preprocess(
      (val) => (typeof val === 'string' ? parseFloat(val) : null),
      z.number().min(0).max(Number.MAX_SAFE_INTEGER)
    ),
  },
  revenuePercentage: {
    name: 'irp',
    schema: z.preprocess(
      (val) => (typeof val === 'string' ? parseFloat(val) : null),
      z
        .number()
        .min(INPUT_VALUE_RANGES.revenuePercentage.min)
        .max(INPUT_VALUE_RANGES.revenuePercentage.max)
    ),
  },
  timePeriod: {
    name: 'itp',
    schema: z.preprocess(
      (val) => (typeof val === 'string' ? parseInt(val, 10) : null),
      z.union([z.literal(3), z.literal(5), z.literal(10)])
    ),
  },
  title: {
    name: 'iti',
    schema: z.string(),
  },
} satisfies Partial<
  Record<
    keyof NarrativeForm,
    { name: string; schema: ZodTypeAny | ZodEffects<ZodTypeAny> }
  >
>;

export const narrativeGetValuatorQueryParamInitialValues =
  (): Partial<NarrativeForm> => {
    if (typeof window === 'undefined') return {};
    const url = new URL(window.location.href);

    const values = {};
    for (const [fieldName, metadata] of Object.entries(
      NARRATIVE_VALUATOR_QUERY_FIELD_MAP
    )) {
      const queryValue = url.searchParams.get(metadata.name);
      if (!queryValue) continue;

      values[fieldName] =
        metadata.schema.safeParse(queryValue).data ?? undefined;
    }

    return values;
  };
